/* eslint-disable max-lines */
import React from 'react';

import Tippy from '@tippyjs/react';

import { TYPE_LABELS } from 'lib/constants/transaction/typeLabels';
import numOrDefault from 'lib/helpers/numOrDefault';
import { formatDateAndTime, mString, pm, snakeToTitleCase } from 'lib/helpers/utility';

import CardProcessor from 'components/shared/CardProcessor';
import { APPOINTMENT_RESOURCE_OPTIONS } from 'components/shared/Filters/lib/filterOptions';
import Actions from 'components/shared/Transactions/components/Actions';

export const getColDefs = (timezone, permissions) => [
  {
    field: 'id',
    headerName: 'ID',
    maxWidth: 120
  },
  {
    field: 'created_at',
    headerName: 'Date',
    valueFormatter: ({ value }) => (value ? formatDateAndTime(value, timezone) : '')
  },
  {
    field: 'status',
    headerName: 'Status',
    cellRenderer: ({ value, data }) => {
      return (
        <Tippy
          className="tippy-dark"
          content={data.status_description}
          disabled={!data.status_description}>
          <p className="w-fit">{snakeToTitleCase(value)}</p>
        </Tippy>
      );
    }
  },
  {
    field: 'type',
    headerName: 'Type',
    valueFormatter: ({ value }) => TYPE_LABELS[value]
  },
  {
    field: 'cc_type',
    headerName: 'Card Type',
    cellRenderer: ({ value }) => (
      <div className="flex h-full items-center justify-center">
        <CardProcessor>{value}</CardProcessor>
      </div>
    )
  },
  { field: 'cc_number', headerName: 'Card Number (Last 4)' },
  ...(pm(permissions, 'payments.read')
    ? [
        {
          field: 'amount_paid_self',
          headerName: 'Amount',
          initialWidth: 260,
          cellRenderer: ({ value, data }) => {
            const surcharge = numOrDefault(data?.surcharge_amount);
            const refunded = numOrDefault(data?.amount_refunded);

            return (
              <Tippy
                className="tippy-dark"
                content={
                  <>
                    <p>Charge: {mString(numOrDefault(value) + refunded - surcharge)}</p>
                    <p>Technology fee: {mString(surcharge)}</p>
                  </>
                }>
                <p>
                  {mString(numOrDefault(value) + refunded)}
                  {!!data?.amount_refunded ? (
                    <span className="text-red-600">
                      &nbsp; (Refunded:
                      <span className="font-bold"> -{mString(refunded)}</span>)
                    </span>
                  ) : null}
                </p>
              </Tippy>
            );
          },
          aggFunc: 'sum',
          sortable: false
        }
      ]
    : []),
  {
    field: 'cashier_name',
    headerName: 'Cashier'
  },
  {
    field: 'note',
    headerName: 'Note'
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: Actions,
    cellClass: 'no-border',
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const DEFAULT_COLUMN_DEFS = {
  enableRowGroup: true
};

export const GRID_OPTIONS = {
  animateRows: true,
  rowGroupPanelShow: 'always',
  enableRangeSelection: true,
  showOpenedGroup: true,
  suppressAggFuncInHeader: true,
  autoGroupColumnDef: {
    pinned: 'left',
    sortable: false
  }
};

const TRANSACTION_STATUS_OPTIONS = [
  {
    label: 'Started',
    value: 'started'
  },
  {
    label: 'Cancelled',
    value: 'cancelled'
  },
  {
    label: 'Completed',
    value: 'completed'
  },
  {
    label: 'Initiated',
    value: 'initiated'
  },
  {
    label: 'Voided',
    value: 'voided'
  },
  {
    label: 'Failed',
    value: 'failed'
  },
  {
    label: 'Refunded',
    value: 'refunded'
  },
  {
    label: 'Partially Refunded',
    value: 'partially_refunded'
  }
];

const TRANSACTION_TYPE_OPTIONS = [
  {
    label: 'Card Present',
    value: 'CARD_PRESENT'
  },
  {
    label: 'In Progress',
    value: 'IN_PROGRESS'
  },
  {
    label: 'Amex',
    value: 'amex'
  },
  {
    label: 'Cash',
    value: 'cash'
  },
  {
    label: 'Online',
    value: 'ONLINE'
  },
  {
    label: 'Charge',
    value: 'charge'
  },
  {
    label: 'Check',
    value: 'check'
  }
];

export const DEFAULT_FILTERS = {
  date: {
    type: 'date-range',
    title: 'Date of Transaction',
    icon: 'calendar-mono'
  },
  status: {
    title: 'Status',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: TRANSACTION_STATUS_OPTIONS.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'transaction_statuses.options',
    icon: 'status-mono'
  },
  type: {
    title: 'Type',
    placeholder: 'Search by type',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: TRANSACTION_TYPE_OPTIONS.filter((type) =>
          type.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'transaction_types.options',
    icon: 'list-unordered-mono'
  },
  appointment_resource: APPOINTMENT_RESOURCE_OPTIONS,
  card_number: {
    title: 'Card Number',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'credit-card-mono'
  },
  check_number: {
    title: 'Check Number',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'card-check-mono'
  },
  amount: {
    type: 'range',
    title: 'Amount',
    icon: 'dollar-square-mono'
  }
  // cashier: {
  //   queryFn: getMembers,
  //   queryKey: 'members',
  //   multiple: true,
  //   title: 'Cashier',
  //   placeholder: 'Search cashier',
  //   formatData: (data) =>
  //     data.map((member) => ({
  //       id: member.user_id,
  //       label:
  //         member?.f_name +
  //         ' ' +
  //         member?.l_name +
  //         `${member?.status !== 'active' ? ` ( ${member?.status} )` : ''}`,
  //       value: member?.id
  //     }))
  // },
  // practice: {
  //   queryFn: getPractices,
  //   queryKey: 'practices',
  //   multiple: true,
  //   title: 'Practice',
  //   placeholder: 'Search practice',
  //   formatData: (data) =>
  //     data.map((practice) => ({
  //       label: practice?.name,
  //       value: practice?.id
  //     }))
  // }
};
